import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import {
  closeActiveMainPanelOverlayAtom,
  closeChatMainPanelOverlayAtom,
  mainPanelOverlayActiveAtom,
  mainPanelOverlayHasAnyAtom,
  type MainPanelOverlayEntry,
} from '@/stores/mainPanelOverlayStore'
import {
  DEFAULT_SCREENATA_CHAT_WIDTH_PX,
  chatPanelOverrideAtom,
  screenataAssistantModeAtom,
  screenataAssistantOpenAtom,
  screenataChatFullscreenAtom,
  screenataSidebarChatTransitioningAtom,
  screenataSidebarChatViewAtom,
} from '@/stores/screenataPageContextStore'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { isWorkspaceChatPath } from '@/lib/screenata-phase'
import { cn } from '@/lib/utils'
import { ScreenataLoader } from '@deepguide-ai/ui/screenata-loader'
import { SidebarInset, SidebarProvider } from '@deepguide-ai/ui/sidebar'
import { AdminModeBanner } from '@/components/AdminModeBanner'
import {
  ViewModeBannerSlot,
  appBannerHeight,
  appViewportHeight,
  viewModeBannerAtom,
} from '@/components/ViewModeBanner'
import { AppSidebar } from '@/components/app-sidebar'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import { PartnerDelegationBanner } from '@/components/partner-delegation-banner'
import { ScreenataWorkspaceChat } from '@/components/screenata/ScreenataWorkspaceChat'
import { IntegrationConnectProvider } from '@/components/screenata/integrations/IntegrationConnectContext'
import {
  MainPanelOverlay,
  MainPanelOverlayHost,
} from '@/components/screenata/main-panel-overlay/MainPanelOverlay'
import { ScreenataSidebarProvider } from '@/components/screenata/screenata-sidebar-context'

export const Route = createFileRoute('/(protected)/workspaces')({
  component: WorkspacesLayout,
})

function WorkspacesLayout() {
  const MIN_CHAT_WIDTH_PX = DEFAULT_SCREENATA_CHAT_WIDTH_PX
  const SPLIT_HANDLE_WIDTH_PX = 8
  const OVERLAY_MOTION_TRANSITION = {
    duration: 0.22,
    ease: [0.4, 0, 0.2, 1] as const,
  }
  const location = useLocation()
  const chatOverride = useAtomValue(chatPanelOverrideAtom)
  const [isAssistantOpen, setAssistantOpen] = useAtom(
    screenataAssistantOpenAtom
  )
  const assistantMode = useAtomValue(screenataAssistantModeAtom)
  const isAssistantFullscreen = useAtomValue(screenataChatFullscreenAtom)
  const activeMainPanelOverlay = useAtomValue(mainPanelOverlayActiveAtom)
  const hasMainPanelOverlay = useAtomValue(mainPanelOverlayHasAnyAtom)
  const closeActiveMainPanelOverlay = useSetAtom(
    closeActiveMainPanelOverlayAtom
  )
  const closeChatMainPanelOverlay = useSetAtom(closeChatMainPanelOverlayAtom)
  const activeWorkspace = useAtomValue(activeWorkspaceWithStorageAtom)
  // One banner today; the height budget is arithmetic if that changes.
  //
  // Must be read HERE, with the other hooks — this component early-returns a
  // loader while the program query is in flight, and a hook below that point is
  // skipped on those renders ("Rendered more hooks than during the previous
  // render").
  const bannerCount = useAtomValue(viewModeBannerAtom) ? 1 : 0

  // Chat-main-view chat-rail width (in px) when an overlay is open.
  // Persisted across reloads. The drag handle in the chat-main-view
  // branch updates this directly.
  const CHAT_MAIN_VIEW_RAIL_KEY = 'screenata:chatMainViewRailPx'
  const CHAT_MAIN_VIEW_RAIL_MIN = 380
  const CHAT_MAIN_VIEW_RAIL_MAX = 900
  const CHAT_MAIN_VIEW_RAIL_DEFAULT = 480
  // Content-page assistant rail width (px), persisted across reloads. Bounded to
  // a share of the container at render time (see workspaceChatRailWidthPx).
  const WORKSPACE_CHAT_RAIL_KEY = 'screenata:workspaceChatRailPx'
  const WORKSPACE_CHAT_RAIL_MIN_PERCENT = 0.2
  const WORKSPACE_CHAT_RAIL_MAX_PERCENT = 0.6
  const [chatMainViewRailPx, setChatMainViewRailPx] = useState<number>(() => {
    if (typeof window === 'undefined') return CHAT_MAIN_VIEW_RAIL_DEFAULT
    const stored = window.localStorage.getItem(CHAT_MAIN_VIEW_RAIL_KEY)
    const parsed = stored ? Number(stored) : NaN
    if (!Number.isFinite(parsed)) return CHAT_MAIN_VIEW_RAIL_DEFAULT
    return Math.min(
      CHAT_MAIN_VIEW_RAIL_MAX,
      Math.max(CHAT_MAIN_VIEW_RAIL_MIN, parsed)
    )
  })
  const chatRailDragStateRef = useRef<{
    startX: number
    startWidth: number
  } | null>(null)
  const workspaceChatRailDragStateRef = useRef<{
    startX: number
    startWidth: number
    containerWidth: number
  } | null>(null)
  const [contentViewportFrame, setContentViewportFrame] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  })
  const [workspaceChatRailPx, setWorkspaceChatRailPx] = useState<number | null>(
    () => {
      if (typeof window === 'undefined') return null
      const stored = window.localStorage.getItem(WORKSPACE_CHAT_RAIL_KEY)
      const parsed = stored ? Number(stored) : NaN
      return Number.isFinite(parsed) ? parsed : null
    }
  )
  // Mirror the latest width into a ref so the drag-end persist reads a fresh
  // value without re-subscribing the mousemove/up listeners every frame.
  const workspaceChatRailPxRef = useRef<number | null>(workspaceChatRailPx)
  workspaceChatRailPxRef.current = workspaceChatRailPx
  const wasAssistantOpenRef = useRef(isAssistantOpen)
  useEffect(() => {
    if (wasAssistantOpenRef.current && !isAssistantOpen) {
      closeChatMainPanelOverlay()
    }
    wasAssistantOpenRef.current = isAssistantOpen

    if (!isAssistantOpen) {
      // Keep the resized width (persisted) so reopening the rail restores it;
      // only clear the in-flight drag state.
      workspaceChatRailDragStateRef.current = null
    }
  }, [closeChatMainPanelOverlay, isAssistantOpen])
  const handleChatRailMouseDown = (e: React.MouseEvent) => {
    chatRailDragStateRef.current = {
      startX: e.clientX,
      startWidth: chatMainViewRailPx,
    }
    e.preventDefault()
  }
  const handleWorkspaceChatRailMouseDown = (e: React.MouseEvent) => {
    const containerWidth =
      contentViewportFrame.width || window.innerWidth || MIN_CHAT_WIDTH_PX
    workspaceChatRailDragStateRef.current = {
      startX: e.clientX,
      startWidth:
        workspaceChatRailPx ?? containerWidth * WORKSPACE_CHAT_RAIL_MIN_PERCENT,
      containerWidth,
    }
    e.preventDefault()
  }
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = chatRailDragStateRef.current
      if (drag) {
        const next = drag.startWidth - (e.clientX - drag.startX)
        const clamped = Math.min(
          CHAT_MAIN_VIEW_RAIL_MAX,
          Math.max(CHAT_MAIN_VIEW_RAIL_MIN, next)
        )
        setChatMainViewRailPx(clamped)
        return
      }

      const workspaceDrag = workspaceChatRailDragStateRef.current
      if (!workspaceDrag) return
      const minWidth =
        workspaceDrag.containerWidth * WORKSPACE_CHAT_RAIL_MIN_PERCENT
      const maxWidth =
        workspaceDrag.containerWidth * WORKSPACE_CHAT_RAIL_MAX_PERCENT
      const next = workspaceDrag.startWidth - (e.clientX - workspaceDrag.startX)
      setWorkspaceChatRailPx(Math.min(maxWidth, Math.max(minWidth, next)))
    }
    const onUp = () => {
      const wasChatMainDrag = chatRailDragStateRef.current
      const wasWorkspaceDrag = workspaceChatRailDragStateRef.current
      chatRailDragStateRef.current = null
      workspaceChatRailDragStateRef.current = null
      try {
        if (wasChatMainDrag) {
          window.localStorage.setItem(
            CHAT_MAIN_VIEW_RAIL_KEY,
            String(chatMainViewRailPx)
          )
        }
        if (wasWorkspaceDrag && workspaceChatRailPxRef.current != null) {
          window.localStorage.setItem(
            WORKSPACE_CHAT_RAIL_KEY,
            String(Math.round(workspaceChatRailPxRef.current))
          )
        }
      } catch {
        // localStorage unavailable; ignore.
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [chatMainViewRailPx])
  const [isScreenataChatViewActive, setScreenataChatViewActive] = useAtom(
    screenataSidebarChatViewAtom
  )
  const isScreenataChatTransitioning = useAtomValue(
    screenataSidebarChatTransitioningAtom
  )
  const normalizedPathname =
    location.pathname !== '/' && location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : location.pathname
  const previousOverlayPathnameRef = useRef(normalizedPathname)
  const previousChatViewPathnameRef = useRef(normalizedPathname)
  const isWorkflowExecutionRoute =
    location.pathname.includes('/workflows/') &&
    location.pathname.includes('/executions/')
  const isWorkspaceSelectionRoute = normalizedPathname === '/workspaces'
  // Full-screen Screenata flows that suppress the global sidebar layout
  // and the workspace chat rail (they ship their own chat rail).
  const screenataWizardSuffixes = ['/connect-repo', '/onboarding', '/setup']

  // Pull program data so we can tell whether the user is still in onboarding.
  // IMPORTANT: scope the query key by `activeWorkspace?.id` to avoid cache
  // collisions on workspace switch. Without the workspace-scoped key,
  // TanStack Query would return the PREVIOUS workspace's program data
  // immediately after a switch (because both workspaces share the same
  // key `[getProgram, { framework: 'SOC2' }]`), and the layout would
  // flip-flop between sidebar / no-sidebar mid-render, reshaping the
  // React tree enough to trigger Radix ScrollArea's composed-ref setState
  // to loop. The parent screenata/route.tsx `beforeLoad` uses the same
  // scoping trick, which is why direct-URL refreshes don't hit this.
  // The chrome-less layout is decided by the URL (`isScreenataRoute`), never by
  // the program's onboarding phase. This shell used to fetch the program and
  // strip the sidebar whenever the phase was 'onboarding'; that branch was
  // inert only because `getScreenataPhase` was stubbed to a constant. Restoring
  // the real function would have removed the sidebar from EVERY page of a
  // workspace that had not finished the wizard — which, now that setup is no
  // longer completed at creation, is every new workspace. The first-run
  // redirect belongs to the workspace-home route (`index.lazy.tsx`), which
  // sends the user to /onboarding, where the URL check removes the chrome.
  const isScreenataRoute =
    activeWorkspace?.type === 'SCREENATA' &&
    screenataWizardSuffixes.some((suffix) =>
      normalizedPathname.endsWith(suffix)
    )
  const isAuditorCenterRoute = normalizedPathname.includes('/auditor-center/')
  // The auditor center hides the Vera chat panel from EXTERNAL auditors only.
  // Company members (e.g. an admin in "view as auditor" mode) keep it mounted —
  // the "Ask Vera" affordances on those pages open this panel, and without it
  // mounted they silently no-op.
  const { isAuditor: isEffectiveAuditor } = useWorkspacePermissions(
    activeWorkspace?.id
  )
  const suppressWorkspaceChat = isAuditorCenterRoute && isEffectiveAuditor
  const isScreenataWorkspaceRoute =
    activeWorkspace?.type === 'SCREENATA' && !isAuditorCenterRoute
  // URL-driven detection of the chat thread page and the workspace
  // home (welcome chat). Used below to force chat-main-view layout
  // for those routes WITHOUT relying on a React state set by the
  // route's component — that would mount/unmount the route in a loop
  // because the chat-main-view branch doesn't render <Outlet />.
  const isThreadChatPath =
    /\/workspaces\/[^/]+\/chat\//.test(normalizedPathname) &&
    !normalizedPathname.includes('/settings/')
  const isWorkspaceHomePath = /\/workspaces\/[^/]+\/?$/.test(normalizedPathname)
  const isDashboardHomeView =
    isScreenataWorkspaceRoute &&
    normalizedPathname === `/workspaces/${activeWorkspace?.id}/dashboard`
  const isHomeSurface =
    isScreenataWorkspaceRoute &&
    (isWorkspaceHomePath || isThreadChatPath || isDashboardHomeView)
  // Mirror the URL-driven chat-main-view flag onto
  // `screenataSidebarChatViewAtom` so downstream consumers (notably
  // `Chat.isWelcomeLayout`, which gates the workspace-home welcome
  // hero with stats / suggestions) fire correctly. The shell layout
  // itself remains URL-driven so stale atom state cannot hide normal
  // child routes.
  const isChatMainViewByUrl = isThreadChatPath || isWorkspaceHomePath
  useEffect(() => {
    setScreenataChatViewActive((prev) =>
      prev === isChatMainViewByUrl ? prev : isChatMainViewByUrl
    )
  }, [isChatMainViewByUrl, setScreenataChatViewActive])
  const [presentMainPanelOverlay, setPresentMainPanelOverlay] =
    useState<MainPanelOverlayEntry | null>(activeMainPanelOverlay)
  const [contentViewportEl, setContentViewportEl] =
    useState<HTMLDivElement | null>(null)
  //以new或者export结尾，并且路径不包括/docs/或/templates/或/runbooks/
  const isNewRoute =
    /\/(new|export)$/.test(location.pathname) &&
    !location.pathname?.includes('/docs/') &&
    !location.pathname?.includes('/templates/') &&
    !location.pathname?.includes('/runbooks/')
  const shouldShowResizableChat =
    !isAuditorCenterRoute &&
    isAssistantOpen &&
    assistantMode === 'sidebar'
  const shouldShowScreenataChatMainView =
    isScreenataWorkspaceRoute &&
    !isScreenataChatTransitioning &&
    (isThreadChatPath || isWorkspaceHomePath)
  const shouldUseResizablePanelLayout =
    shouldShowResizableChat && !shouldShowScreenataChatMainView
  const shouldShowFullscreenChat =
    shouldShowResizableChat && isAssistantFullscreen
  const shouldShowResizableWorkspaceChat =
    shouldUseResizablePanelLayout && !shouldShowFullscreenChat
  const displayedMainPanelOverlay =
    activeMainPanelOverlay ?? presentMainPanelOverlay
  const hasAnimatedMainPanelOverlay = displayedMainPanelOverlay !== null
  const shouldRenderAnimatedMainPanelOverlay =
    activeMainPanelOverlay !== null && displayedMainPanelOverlay !== null
  const animatedOverlayWidth = hasAnimatedMainPanelOverlay
    ? Math.max(
        0,
        contentViewportFrame.width - chatMainViewRailPx - SPLIT_HANDLE_WIDTH_PX
      )
    : 0
  const animatedChatWidth =
    contentViewportFrame.width > 0
      ? hasMainPanelOverlay
        ? chatMainViewRailPx
        : contentViewportFrame.width
      : contentViewportFrame.width || undefined

  // Content-page assistant rail width, clamped to [20%, 60%] of the container
  // (and never below MIN_CHAT_WIDTH_PX) so a persisted value can't overflow a
  // narrower viewport. Single source of truth for the rail render + panel anchor.
  const workspaceChatRailWidthPx = (() => {
    const container =
      contentViewportFrame.width ||
      (typeof window !== 'undefined' ? window.innerWidth : 0) ||
      MIN_CHAT_WIDTH_PX
    const minW = Math.max(
      MIN_CHAT_WIDTH_PX,
      container * WORKSPACE_CHAT_RAIL_MIN_PERCENT
    )
    const maxW = container * WORKSPACE_CHAT_RAIL_MAX_PERCENT
    const raw = workspaceChatRailPx ?? minW
    return Math.min(Math.max(raw, minW), maxW)
  })()

  // Live width of the docked assistant rail, so the floating "Get audit-ready"
  // panel anchors beside it (not under it). 0 when no resizable rail is docked
  // — on chat-main-view routes the chat is the main panel, not a side rail.

  useEffect(() => {
    if (activeMainPanelOverlay) {
      setPresentMainPanelOverlay(activeMainPanelOverlay)
    }
  }, [activeMainPanelOverlay])

  // System-detail previews belong to the page that opened them. Clear them
  // before the destination paints so workspace navigation never carries a
  // stale detail surface onto the next page.
  useLayoutEffect(() => {
    const previousPathname = previousOverlayPathnameRef.current
    previousOverlayPathnameRef.current = normalizedPathname

    if (
      previousPathname !== normalizedPathname &&
      activeMainPanelOverlay?.kind === 'system-detail'
    ) {
      closeActiveMainPanelOverlay()
    }
  }, [activeMainPanelOverlay, closeActiveMainPanelOverlay, normalizedPathname])

  useEffect(() => {
    if (previousChatViewPathnameRef.current === normalizedPathname) return

    const prevPathname = previousChatViewPathnameRef.current
    previousChatViewPathnameRef.current = normalizedPathname

    if (isScreenataChatTransitioning) return

    // Workspace root → /chat/$id transitions (auto-thread-creation on
    // first user message) must NOT toggle the chat-view atom. Flipping
    // it false mid-transition unmounts useChat and wipes in-flight
    // messages.
    if (
      isWorkspaceChatPath(prevPathname) &&
      isWorkspaceChatPath(normalizedPathname)
    ) {
      return
    }

    if (
      isAssistantOpen &&
      isWorkspaceChatPath(prevPathname) &&
      !isWorkspaceChatPath(normalizedPathname)
    ) {
      setAssistantOpen(false)
    }

    if (isScreenataChatViewActive) {
      setScreenataChatViewActive(false)
    }
  }, [
    isAssistantOpen,
    isScreenataChatTransitioning,
    isScreenataChatViewActive,
    normalizedPathname,
    setAssistantOpen,
    setScreenataChatViewActive,
  ])

  useLayoutEffect(() => {
    const container = contentViewportEl
    if (!container) return

    const updateWidth = () => {
      const rect = container.getBoundingClientRect()
      setContentViewportFrame({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      })
    }

    updateWidth()

    const observer = new ResizeObserver(() => updateWidth())
    observer.observe(container)

    return () => observer.disconnect()
  }, [contentViewportEl])

  const insetClassName = cn(
    // `h-[var(--app-vh)]` not `h-screen`: with a view-mode banner above, the
    // viewport is no longer the space available to the inset.
    'shrink-0 overflow-hidden',
    isHomeSurface ? 'h-full bg-card' : 'h-[var(--app-vh,100svh)]',
    'min-w-0 flex-1'
  )
  const mainContent = isScreenataChatTransitioning ? (
    <div className='bg-background flex h-full w-full items-center justify-center'>
      <ScreenataLoader size={40} className='text-status-blue' />
    </div>
  ) : (
    <MainPanelOverlayHost>
      <ScreenataPageLayout
        scrollViewClassName='h-full'
        title={isDashboardHomeView ? 'Home' : undefined}
        hideHeader={isDashboardHomeView}
        scroll={isDashboardHomeView ? true : undefined}
      >
        <Outlet />
      </ScreenataPageLayout>
    </MainPanelOverlayHost>
  )
  // Wrap everything in SidebarProvider to prevent useSidebar errors
  // but only show the actual sidebar layout for routes that need it

  return (
    <Suspense
      fallback={
        isNewRoute ||
        isWorkflowExecutionRoute ||
        isScreenataRoute ||
        isWorkspaceSelectionRoute ? (
          // Full-screen routes have no sidebar — center the loader.
          <div className='flex h-screen w-screen items-center justify-center'>
            <ScreenataLoader size={40} className='text-status-blue' />
          </div>
        ) : (
          // Sidebar routes: mirror the eventual shell — a sidebar-width
          // rail + its separator — and center the loader in the CONTENT
          // region. Otherwise the loader sits at viewport-center during
          // load, then jumps right when the real sidebar mounts. The
          // sidebar always starts expanded on reload (SidebarProvider
          // defaultOpen=true, no cookie restore), so 13.75rem matches;
          // the rail is desktop-only like the real one.
          <div className='flex h-screen w-screen'>
            {/* <div
              className='hidden w-[13.75rem] shrink-0 border-r border-sidebar-border bg-sidebar md:block'
              aria-hidden='true'
            /> */}
            <div className='flex w-screen h-screen items-center justify-center'>
              <ScreenataLoader size={40} className='text-status-blue' />

            </div>
          </div>
        )
      }
    >
      {/* View-mode banner spans the ENTIRE window, sidebar included.
          It cannot live inside `SidebarProvider` — that renders a horizontal
          flex (`group/sidebar-wrapper`), so anything inside it starts to the
          right of the sidebar. `AdminModeBanner` below has exactly that bug.
          The shell that knows the mode publishes it (see
          `usePublishViewModeBanner`); this renders it.
          `--app-vh` is the height budget below the banner: `h-screen` and
          `min-h-svh` are viewport-absolute and would each overflow by the
          banner's height otherwise. */}
      <div
        className='flex flex-col'
        style={{
          height: '100svh',
          ['--app-vh' as string]: appViewportHeight(bannerCount),
          // The sidebar container is `fixed`, so it needs an explicit top
          // offset — a flex wrapper cannot move it.
          ['--app-banner-h' as string]: appBannerHeight(bannerCount),
        }}
      >
        <ViewModeBannerSlot />
        <div className='min-h-0 min-w-0 flex-1'>
      <SidebarProvider mobileMode='icon' className='h-full min-h-0'>
        <IntegrationConnectProvider>
          <ScreenataSidebarProvider>
            {isWorkspaceSelectionRoute ? (
              <Outlet />
            ) : isScreenataRoute ? (
              // Full-screen, no-sidebar Screenata wizard routes (/onboarding,
              // /connect-repo, /setup). Their children (OnboardingWizard and
              // friends) use min-h-full / h-full and a [rail | content]
              // flex row, which collapse without a parent that has an explicit
              // height — give them a real-height scroll container so they fill
              // and center correctly. isScreenataRoute is URL-based and matches
              // even before program phase resolves to 'onboarding'.
              <div className='h-[var(--app-vh,100svh)] w-full overflow-y-auto'>
                <Outlet />
              </div>
            ) : isNewRoute || isWorkflowExecutionRoute ? (
              // For new route and workflow execution, just render the outlet
              // without sidebar layout.
              <>
                <Outlet />
                {!suppressWorkspaceChat && <ScreenataWorkspaceChat />}
              </>
            ) : (
              // For other routes, render the full sidebar layout
              <>
                <AppSidebar />
                <div
                  ref={setContentViewportEl}
                  className={cn(
                    'flex min-w-0 flex-1 overflow-y-hidden',
                    shouldShowScreenataChatMainView
                      ? 'overflow-x-hidden'
                      : 'overflow-x-auto'
                  )}
                >
                  <div
                    className={cn(
                      'relative flex h-[var(--app-vh,100svh)] min-w-full flex-col',
                      isHomeSurface && 'bg-card'
                    )}
                    style={{
                      width: undefined,
                      minWidth: undefined,
                    }}
                  >
                    <div className='relative flex min-h-0 min-w-0 flex-1'>
                    {shouldShowScreenataChatMainView ? (
                      // "Chat-main-view" pages (welcome / dedicated chat
                      // thread) render the chat full-width by default,
                      // and split into [overlay | chat] when a draft
                      // overlay is active (#364). The flex container
                      // shape is stable across this transition so
                      // `<ScreenataWorkspaceChat>`'s ChatPanel stays
                      // mounted — the conversation survives the slide.
                      //
                      // The route's <Outlet /> is rendered hidden so the
                      // matched route's component (which syncs URL state
                      // like `currentThreadIdAtom`) still mounts and runs
                      // its effects. Its component returns null, so the
                      // hidden div has no visual content.
                      <SidebarInset className='h-full min-w-0 flex-1 overflow-hidden bg-card'>
                        <AdminModeBanner />
                        <PartnerDelegationBanner />
                        <div className='hidden'>
                          <Outlet />
                        </div>
                        <div className='flex min-h-0 w-full flex-1 overflow-hidden'>
                          <AnimatePresence
                            initial={false}
                            onExitComplete={() =>
                              setPresentMainPanelOverlay(null)
                            }
                          >
                            {shouldRenderAnimatedMainPanelOverlay && (
                              <motion.div
                                key={displayedMainPanelOverlay!.id}
                                initial={{ width: 0 }}
                                animate={{ width: animatedOverlayWidth }}
                                exit={{ width: 0 }}
                                transition={OVERLAY_MOTION_TRANSITION}
                                className='relative min-h-0 shrink-0 overflow-hidden'
                                aria-hidden={!hasMainPanelOverlay}
                              >
                                <div
                                  className='h-full'
                                  style={{ width: animatedOverlayWidth }}
                                >
                                  <MainPanelOverlay
                                    entryOverride={displayedMainPanelOverlay!}
                                    forceVisible
                                    interactiveOverride={hasMainPanelOverlay}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                          <AnimatePresence initial={false}>
                            {hasMainPanelOverlay && (
                              // Drag handle — only useful when the overlay
                              // is showing, otherwise there's nothing to
                              // resize against. 1px hit-target widened on
                              // hover; updates `chatMainViewRailPx` state
                              // (persisted to localStorage).
                              <motion.div
                                role='separator'
                                aria-orientation='vertical'
                                onMouseDown={handleChatRailMouseDown}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.12 }}
                                className='group relative z-10 -mx-1 w-2 shrink-0 cursor-col-resize'
                              >
                                <div className='bg-border group-hover:bg-border-divider-hover absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors' />
                              </motion.div>
                            )}
                          </AnimatePresence>
                          <motion.div
                            initial={false}
                            animate={
                              animatedChatWidth
                                ? { width: animatedChatWidth }
                                : undefined
                            }
                            transition={OVERLAY_MOTION_TRANSITION}
                            className={cn(
                              'flex h-full min-h-0 flex-col overflow-hidden',
                              hasAnimatedMainPanelOverlay
                                ? 'shrink-0'
                                : 'min-w-0 flex-1'
                            )}
                          >
                            <ScreenataWorkspaceChat
                              fillParentWhenOpen
                              fullscreenView
                              forceWelcomeLayout={isWorkspaceHomePath}
                            />
                          </motion.div>
                        </div>
                      </SidebarInset>
                    ) : shouldShowResizableWorkspaceChat ? (
                      <div className={cn('flex min-w-full overflow-hidden', isHomeSurface ? 'h-full' : 'h-screen')}>
                        <div className='min-w-0 flex-1 overflow-hidden'>
                          <SidebarInset className={insetClassName}>
                            <div className='h-full'>
                              <AdminModeBanner />
                              <PartnerDelegationBanner />
                              {mainContent}
                            </div>
                          </SidebarInset>
                        </div>
                        <div
                          role='separator'
                          aria-orientation='vertical'
                          onMouseDown={handleWorkspaceChatRailMouseDown}
                          className='group relative z-10 -mx-1 w-2 shrink-0 cursor-col-resize'
                        >
                          <div className='bg-border group-hover:bg-border-divider-hover absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors' />
                        </div>
                        <div
                          className={cn('min-w-0 shrink-0 overflow-hidden', isHomeSurface ? 'h-full bg-card' : 'h-screen bg-background')}
                          style={{ width: `${workspaceChatRailWidthPx}px` }}
                        >
                          <ScreenataWorkspaceChat
                            fillParentWhenOpen
                            allowFullscreen
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <SidebarInset className={insetClassName}>
                          <div className='h-full'>
                            <AdminModeBanner />
                            <PartnerDelegationBanner />
                            {mainContent}
                          </div>
                        </SidebarInset>
                        {shouldUseResizablePanelLayout && (
                          <div
                            className={cn(
                              'shrink-0 border-l border-border',
                              isHomeSurface ? 'h-full bg-card' : 'h-screen bg-background',
                              shouldShowFullscreenChat &&
                                'fixed z-20 min-w-0 overflow-hidden border-l-0'
                            )}
                            style={{
                              width: shouldShowFullscreenChat
                                ? `${contentViewportFrame.width}px`
                                : `${MIN_CHAT_WIDTH_PX}px`,
                              minWidth: shouldShowFullscreenChat
                                ? undefined
                                : `${MIN_CHAT_WIDTH_PX}px`,
                              left: shouldShowFullscreenChat
                                ? `${contentViewportFrame.left}px`
                                : undefined,
                              top: shouldShowFullscreenChat
                                ? `${contentViewportFrame.top}px`
                                : undefined,
                              height: shouldShowFullscreenChat
                                ? `${contentViewportFrame.height}px`
                                : undefined,
                            }}
                          >
                            <ScreenataWorkspaceChat
                              fillParentWhenOpen
                              allowFullscreen
                            />
                          </div>
                        )}
                        {!shouldUseResizablePanelLayout &&
                          !suppressWorkspaceChat && <ScreenataWorkspaceChat />}
                      </>
                    )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </ScreenataSidebarProvider>
        </IntegrationConnectProvider>
      </SidebarProvider>
        </div>
      </div>
    </Suspense>
  )
}
